import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { CompanyDocument, downloadFile } from "winslow-core";
import { apiGetCompanyDocumentsForWorkspace, apiUploadCompanyDocumentsForWorkspace, apiUpdateCompanyDocument, apiDeleteCompanyDocument } from "../../../services/CompanyDocumentsService";
import { selectUser } from "../../../redux/features/appData/appDataSlice";
import { WazToolbar } from "../toolbar/WazToolbar";
import { AppDocumentsUploadButton } from "../../app/app-documents/AppDocumentsUploadButton";
import { AppChanges } from "../../app/app-changes/AppChanges";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import "./WazDocuments.css";
import Modal from "../../shared/Modal";


type WazDocumentsProps = {};

type UploadInfo = {
    name: string;
    description: string;
    requiresSignatures: boolean;
};

export const WazDocuments = ({}: WazDocumentsProps) => {
    const storeUser = useSelector(selectUser);
    const [documents, setDocuments] = useState<CompanyDocument[] | undefined>(undefined);
    const [filter, setFilter] = useState("");
    const [addDocument, setAddDocument] = useState(false);
    const [info, setInfo] = useState<UploadInfo>({ name: "", description: "", requiresSignatures: false });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [editDocumentId, setEditDocumentId] = useState<string | null>(null);
    const [editInfo, setEditInfo] = useState<UploadInfo>({ name: "", description: "", requiresSignatures: false });
    const [deletingDocument, setDeletingDocument] = useState<CompanyDocument | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            setDocuments(undefined);
            return;
        }
        (async () => {
            const docs = await apiGetCompanyDocumentsForWorkspace(storeUser.currentWorkspaceId || "");
            if (docs) {
                docs.sort((a, b) => a.name.localeCompare(b.name));
                setDocuments(docs);
            }
        })();
    }, [storeUser]);

    const filteredDocuments = documents?.filter((doc) => {
        const search = filter.toLowerCase();
        return (
            doc.name.toLowerCase().includes(search) ||
            (doc.fileName && doc.fileName.toLowerCase().includes(search)) ||
            (doc.description && doc.description.toLowerCase().includes(search))
        );
    });

    const handleSelectedFile = (file: File) => {
        setSelectedFile(file);
    };

    const handleUploadSubmit = async () => {
        if (!storeUser?.currentWorkspaceId) {
            toast("No workspace selected", toastFail);
            return;
        }
        if (!selectedFile) {
            toast("No file selected", toastFail);
            return;
        }
        if (!info.name) {
            toast("Please provide a document name", toastFail);
            return;
        }
        setUploading(true);
        const payload: Partial<CompanyDocument> = {
            workspaceId: storeUser.currentWorkspaceId,
            name: info.name,
            description: info.description,
            requiresSignatures: info.requiresSignatures,
        };
        const response = await apiUploadCompanyDocumentsForWorkspace(
            storeUser.currentWorkspaceId, payload, selectedFile
        );
        setUploading(false);
        if (!response) {
            toast("Error creating company document", toastFail);
            return;
        }
        const companyDocuments = response.data.data as CompanyDocument[];
        setAddDocument(false);
        setInfo({ name: "", description: "", requiresSignatures: false });
        setSelectedFile(null);
        setDocuments((prev) => {
            const updated = prev ? [...prev, ...companyDocuments] : companyDocuments;
            updated.sort((a, b) => a.name.localeCompare(b.name));
            return updated;
        });
    };

    const handleEditClick = (doc: CompanyDocument) => {
        setEditDocumentId(doc.id);
        setEditInfo({
            name: doc.name,
            description: doc.description || "",
            requiresSignatures: doc.requiresSignatures || false,
        });
    };

    const handleEditSubmit = async () => {
        if (!editDocumentId) return;
        setUploading(true);
        const payload: Partial<CompanyDocument> = {
            id: editDocumentId,
            name: editInfo.name,
            description: editInfo.description,
            requiresSignatures: editInfo.requiresSignatures,
        };
        const updatedDocument = await apiUpdateCompanyDocument(payload);
        setUploading(false);
        if (!updatedDocument) {
            toast("Error updating company document", toastFail);
            return;
        }
        setEditDocumentId(null);
        setEditInfo({ name: "", description: "", requiresSignatures: false });
        setDocuments((prev) => {
            if (!prev) return prev;
            const updated = prev.map((doc) =>
                doc.id === updatedDocument.id ? { ...doc, ...updatedDocument } : doc
            );
            updated.sort((a, b) => a.name.localeCompare(b.name));
            return updated;
        });
    };

    const handleDeleteClick = (doc: CompanyDocument) => {
        setDeletingDocument(doc);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingDocument) return;
        setUploading(true);
        const response = await apiDeleteCompanyDocument(deletingDocument.id);
        setUploading(false);
        setShowDeleteModal(false);
        setDeletingDocument(null);
        if (!response) {
            toast("Error deleting company document", toastFail);
            return;
        }
        setDocuments((prev) => prev?.filter((d) => d.id !== response.id));
    };

    const allowSave = !uploading && info?.name && selectedFile;
    const allowEditSave = !uploading && editInfo?.name;

    return (
        <div className="wazawan-documents">
            <WazToolbar />
            <div className="waz-documents-content">
                <h1 className="waz-documents-title">Company Documents</h1>
                <div className="waz-documents-toolbar-row">
                    <input
                        type="text"
                        className="waz-documents-filter-input"
                        placeholder="Filter by name, filename, or description..."
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />
                    <button
                        className="button-save waz-documents-add-btn"
                        onClick={() => setAddDocument((v) => !v)}
                        disabled={addDocument || !!editDocumentId}
                    >
                        + Add Document
                    </button>
                </div>
                {/* Edit Document Form (above table, same style as add) */}
                {editDocumentId && (
                    <div className="waz-documents-form-card">
                        <h2 className="waz-documents-form-title">Edit Document</h2>
                        <div className="waz-documents-form-fields">
                            <label className="waz-documents-label">Name</label>
                            <input
                                type="text"
                                className="waz-documents-input"
                                value={editInfo.name}
                                placeholder="Document Name"
                                onChange={e => setEditInfo({ ...editInfo, name: e.target.value })}
                            />
                            <label className="waz-documents-label">Description</label>
                            <textarea
                                className="waz-documents-textarea"
                                value={editInfo.description}
                                placeholder="Description"
                                rows={3}
                                onChange={e => {
                                    const textarea = e.target as HTMLTextAreaElement;
                                    textarea.style.height = "auto";
                                    textarea.style.height = textarea.scrollHeight + "px";
                                    setEditInfo({ ...editInfo, description: textarea.value });
                                }}
                            />
                            <label className="waz-documents-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="app-custom-property-checkboxsingle"
                                    checked={editInfo.requiresSignatures}
                                    onChange={e => setEditInfo({ ...editInfo, requiresSignatures: e.target.checked })}
                                />
                                Requires Signatures
                            </label>
                        </div>
                        <AppChanges
                            submitText={uploading ? "Saving..." : "Save Changes"}
                            saveDisabled={!allowEditSave}
                            onSubmit={handleEditSubmit}
                            onCancel={() => {
                                setEditDocumentId(null);
                                setEditInfo({ name: "", description: "", requiresSignatures: false });
                            }}
                        />
                    </div>
                )}
                {/* Add Document Form (above table) */}
                {addDocument && (
                    <div className="waz-documents-form-card">
                        <h2 className="waz-documents-form-title">Add Document</h2>
                        <div className="waz-documents-form-fields">
                            <label className="waz-documents-label">Name</label>
                            <input
                                type="text"
                                className="waz-documents-input"
                                value={info.name}
                                placeholder="Document Name"
                                onChange={e => setInfo({ ...info, name: e.target.value })}
                            />
                            <label className="waz-documents-label">Description</label>
                            <textarea
                                className="waz-documents-textarea"
                                value={info.description}
                                placeholder="Description"
                                rows={3}
                                onChange={e => {
                                    const textarea = e.target as HTMLTextAreaElement;
                                    textarea.style.height = "auto";
                                    textarea.style.height = textarea.scrollHeight + "px";
                                    setInfo({ ...info, description: textarea.value });
                                }}
                            />
                            <label className="waz-documents-checkbox-label">
                                <input
                                    type="checkbox"
                                    className="app-custom-property-checkboxsingle"
                                    checked={info.requiresSignatures}
                                    onChange={e => setInfo({ ...info, requiresSignatures: e.target.checked })}
                                />
                                Requires Signatures
                            </label>
                            <div>
                                <AppDocumentsUploadButton
                                    label={selectedFile ? "Change File" : "Select File"}
                                    onFileSelect={handleSelectedFile}
                                    disabled={uploading}
                                />
                                {selectedFile && (
                                    <div style={{ marginTop: 8, color: "#888" }}>{selectedFile.name}</div>
                                )}
                            </div>
                        </div>
                        <AppChanges
                            submitText={uploading ? "Uploading..." : "Add Document"}
                            saveDisabled={!allowSave}
                            onSubmit={handleUploadSubmit}
                            onCancel={() => {
                                setAddDocument(false);
                                setInfo({ name: "", description: "", requiresSignatures: false });
                                setSelectedFile(null);
                            }}
                        />
                    </div>
                )}
                <div className="waz-documents-table-container">
                    <table className="waz-documents-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Filename</th>
                                <th>Description</th>
                                <th>Public</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocuments && filteredDocuments.length > 0 ? (
                                filteredDocuments.map((doc) => (
                                    <tr key={doc.id} className="waz-documents-table-row">
                                        <td>{doc.name}</td>
                                        <td>{doc.fileName}</td>
                                        <td>{doc.description || ""}</td>
                                        <td>{doc.isPublic ? "Yes" : "No"}</td>
                                        <td className="waz-documents-table-actions">
                                            {doc.cloudURL && (
                                                <button
                                                    className="button-save"
                                                    onClick={() => downloadFile(doc.cloudURL, doc.fileName || "document")}
                                                >
                                                    Download
                                                </button>
                                            )}
                                            <button
                                                className="button-cancel"
                                                onClick={() => handleEditClick(doc)}
                                                disabled={!!editDocumentId || addDocument}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="button-cancel"
                                                onClick={() => handleDeleteClick(doc)}
                                                disabled={!!editDocumentId || addDocument}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="waz-documents-table-empty">
                                        No documents found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                style={null}
                canClose={false}
                allowClickout={true}
            >
                <div id="modal-delete-document-confirm">
                    <div className="modal-delete-document-confirm-title">
                        Delete this Document
                    </div>
                    <div className="modal-delete-document-confirm-content">
                        This document will be permanently deleted.
                    </div>
                    <div className="modal-delete-document-confirm-footer">
                        <div className="modal-delete-document-confirm-buttons">
                            <button
                                className="button-cancel"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="button-save"
                                onClick={handleDeleteConfirm}
                                disabled={uploading}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}